import type ExcelJS from "exceljs";

import type {
    Editable,
    OverwriteMediaItem,
    EditableValue,
    OverwriteScopes,
    ArrayEditableValue,
    EditableWithOverwrites,
    CSSSettings
} from "hoxton-types";

import { EditableType } from "hoxton-shared/enums/editables";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";

import type { EditableOverwrite } from "@/types/types";
import { apolloClient } from "@/apollo/index";
import store from "@/store";

import { deepClone, isValidHttpUrl } from "@/utils";
import { ExportableEditableTypes, ExportableTranslationTypes } from "@/enums/editables";
import { ExportGroupScopeLabel } from "@/enums/overwrites";
import * as editableUtils from "@/utils/editables";
import downloadFile from "@/utils/downloadFile";
import mediaItemQuery from "@/apollo/queries/MediaItem.gql";
import { campaignDefaultLanguage, CampaignGetters } from "@/store/modules/campaign";
import { EditorAction, EditorGetters } from "@/store/modules/editor";
import { mimetype } from "@/enums/mimetype";
import excelAddress from "@/utils/excelAddress";

export type OverwriteWithEditable = {
    editable: Editable;
    scope: OverwriteScopes;
    editableGroupValueIds: string[];
    masterTemplateId?: string | null;
    language: string;
    value: EditableValue | null | undefined;
    mediaItem: OverwriteMediaItem | null;
    settings?: CSSSettings | null;
    defaultValue?: EditableValue | null;
    modified?: number;
    __typename?: "Overwrite";
};

declare module "exceljs" {
    interface Worksheet {
        dataValidations: {
            model: Record<string, DataValidation>;
        };
    }
}

// https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
const urlRegex = /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/;
const applyStyle = (element: ExcelJS.Row | ExcelJS.Cell, style: Partial<ExcelJS.Style>) => {
    Object.assign(element, style);
};
const oldHogarthLogo =
    "data:image/png;base64,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";

const defaultBgColor = { argb: "FFFFFFFF" };
const defaultFontColor = { argb: "FF515A6E" };
const headerBgColor = { argb: "FFF1F2F5" };
const infoBgColor = { argb: "FFE7E6E6" };
const logoBgColor = { argb: "FFFFFFFF" };
const defaultFont = { name: "Helvetica Neue", family: 2, color: defaultFontColor, size: 14 };
const silkaFont = { name: "Silka", family: 2, color: defaultFontColor, size: 14, bold: false };
const headerFont = silkaFont;
const defaultBorderStyle = { style: "thin" as const, color: { argb: "FFDCDCDC" } };
const defaultFillPattern = {
    type: "pattern" as const,
    pattern: "solid" as const,
    bgColor: defaultBgColor,
    fgColor: defaultBgColor
};
const defaultBorder = {
    border: {
        top: defaultBorderStyle,
        left: defaultBorderStyle,
        bottom: defaultBorderStyle,
        right: defaultBorderStyle
    }
};
const defaultFormat = {
    numFmt: "@"
};

const styleDefaults = {
    ...defaultBorder,
    ...defaultFormat
};
const headerStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern, bgColor: headerBgColor, fgColor: headerBgColor },
    alignment: { vertical: "middle" as const, horizontal: "center" as const },
    font: {
        ...headerFont,
        size: 14,
        italic: false,
        bold: false
    }
} as const;

const logoCellStyle = {
    ...headerStyle,
    fill: { ...defaultFillPattern, bgColor: logoBgColor, fgColor: logoBgColor }
} as const;

const groupNameStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern, bgColor: headerBgColor, fgColor: headerBgColor },
    alignment: { vertical: "top" as const, horizontal: "left" as const },
    font: {
        ...defaultFont,
        italic: false,
        bold: false
    }
} as const;

const languageGroupNameStyle = {
    ...styleDefaults,
    ...groupNameStyle,
    alignment: { vertical: "top" as const, horizontal: "left" as const }
} as const;

const infoRowStyle = {
    height: 96,
    fill: { ...defaultFillPattern, bgColor: infoBgColor, fgColor: infoBgColor },
    alignment: { vertical: "middle" as const, horizontal: "left" as const, wrapText: true },
    font: {
        ...silkaFont,
        size: 20,
        bold: true
    }
};
const valueStyle = {
    ...styleDefaults,
    fill: { ...defaultFillPattern },
    alignment: { vertical: "top" as const, horizontal: "left" as const },
    font: {
        ...defaultFont,
        italic: false
    }
} as const;

const logoPosition = {
    tl: { col: 0.44, row: 0.21 },
    ext: { width: 220, height: 88 }
};

const setHeaderRowStyle = (sheetObj: ExcelJS.Worksheet, rowNumber: number) => {
    const headerRow = sheetObj.getRow(rowNumber);
    applyStyle(headerRow, headerStyle);
};

const setInfoRowStyle = (sheetObj: ExcelJS.Worksheet, rowNumber: number) => {
    const infoRow = sheetObj.getRow(rowNumber);
    applyStyle(infoRow, infoRowStyle);
    const infoCell = infoRow.getCell(1);
    applyStyle(infoCell, logoCellStyle);
};

interface DataValidation {
    allowBlank: boolean;
    error: string;
    errorTitle: string;
    formulae: string[];
    showErrorMessage: boolean;
    type: "list";
}

type ExportType = "global" | "template";
type OverwriteScopeType = (typeof OverwriteScope)[keyof typeof OverwriteScope];

interface NavigatorWithMsSaveBlob extends Navigator {
    msSaveBlob?: (blob: Blob, fileName: string) => void;
}

interface WorksheetConfig {
    masterTemplateLabelMap: Record<string, string[]>;
    masterTemplateLabels: string[];
    masterTemplateIds: string[];
    exportType: ExportType;
}

interface LanguageUpdateConfig {
    editableGroups: Record<string, { editables: string[]; editableGroupValues: string[] }>;
    editables: Record<string, Editable>;
    masterTemplateIds: string[];
    language: string;
    languageMapping: Record<string, string>;
    normalizedLanguageMapping: Record<string, string>;
    canSeeRestrictedEditables: boolean;
}

const addInfoRow = (
    sheet: ExcelJS.Worksheet,
    fixedColumns: string[],
    headersColumns: { name: string }[],
    hogarthLogo: number,
    title: string,
    subtitle: string
) => {
    sheet.columns = [
        ...fixedColumns.map(name => ({ key: name, width: 40, style: groupNameStyle })),
        ...headersColumns.map(header => ({
            key: header.name,
            width: 40,
            style: valueStyle
        }))
    ];

    sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
    sheet.addRow(["", ""]);
    setInfoRowStyle(sheet, 1);
    sheet.mergeCells(1, 2, 1, headersColumns.length + 1);
    sheet.getCell(1, 2).value = {
        richText: [
            {
                font: {
                    ...silkaFont,
                    bold: true,
                    size: 20
                },
                text: `${title} \r\n`
            },
            {
                font: {
                    ...silkaFont
                },
                text: subtitle
            }
        ]
    };

    sheet.addImage(hogarthLogo, logoPosition);
};

const addValuesAndFilters = (
    columnValues: string[],
    valuesSheet: ExcelJS.Worksheet,
    inputSheet: ExcelJS.Worksheet,
    valueColumnIndex: number,
    inputColumnIndex: number
) => {
    columnValues.forEach((valueName: string, valueIndex: number) => {
        valuesSheet.getCell(valueIndex + 3, valueColumnIndex).value = valueName;
    });

    const rangeStart = excelAddress.encodeAddress(3, inputColumnIndex);
    const rangeEnd = excelAddress.encodeAddress(9999, inputColumnIndex);

    const formulaeRangeLetter = excelAddress.encodeAddress(1, valueColumnIndex).replace("1", "");
    const dataValidation: DataValidation = {
        allowBlank: false,
        error: "Please use the drop down to select a valid value",
        errorTitle: "Invalid Selection",
        formulae: [`Values!$${formulaeRangeLetter}$3:$${formulaeRangeLetter}$${columnValues.length + 2}`],
        showErrorMessage: true,
        type: "list"
    };
    inputSheet.dataValidations.model[`${rangeStart}:${rangeEnd}`] = dataValidation;
};

function addReportingLabelsColumn(
    valuesSheet: ExcelJS.Worksheet,
    inputSheet: ExcelJS.Worksheet,
    valueColumnIndex: number,
    inputColumnIndex: number
) {
    valuesSheet.getColumn(valueColumnIndex).key = "reportingLabels";
    valuesSheet.getCell(2, valueColumnIndex).value = "reportingLabels";
    valuesSheet.getColumn(valueColumnIndex).width = 40;

    inputSheet.getColumn(inputColumnIndex).key = "reportingLabels";
    inputSheet.getCell(2, inputColumnIndex).value = "reportingLabels";
    inputSheet.getColumn(inputColumnIndex).width = 40;
}

function getArrayValue(val: ArrayEditableValue[]) {
    const selectedOption = val.find(option => option.selected) ?? { label: "" };
    return selectedOption.label;
}

function normalizeTranslationPhrase(str: string) {
    return str.toLowerCase().replace(/\W/g, "");
}

function createFile(blob: Blob, exportedFilename: string) {
    const navigatorWithMsSaveBlob = navigator as NavigatorWithMsSaveBlob;

    if (navigatorWithMsSaveBlob.msSaveBlob) {
        // IE 10+
        navigatorWithMsSaveBlob.msSaveBlob(blob, exportedFilename);
        return;
    }

    const url = URL.createObjectURL(blob);

    downloadFile(url, exportedFilename);

    URL.revokeObjectURL(url);
}

/**
 * Restricted editables are hidden from users without the `canManageRestrictedEditables` permission, and the API
 * rejects any save that overwrites them. Exporting them would produce a spreadsheet that cannot be imported back
 * and saved, so they are left out of the export for those users.
 */
export function getExportableEditables(editables: Editable[], canSeeRestrictedEditables: boolean) {
    return editables.filter(
        editable =>
            ExportableEditableTypes.includes(editable.type) && (canSeeRestrictedEditables || !editable.restricted)
    );
}

export function getExportValueFromOverwrite({
    editable,
    masterTemplateId,
    overwrite,
    campaignMasterTemplateIds
}: {
    editable: Editable;
    masterTemplateId: string;
    overwrite: EditableOverwrite | null;
    campaignMasterTemplateIds?: string[];
}) {
    let value = editableUtils.computeEditableValue(editable, masterTemplateId, overwrite);

    const getMediaItemValue = (ov: EditableOverwrite | null) => {
        if (ov?.mediaItem) {
            return `:hoxton/${ov.mediaItem.id}/${ov.mediaItem.name}`;
        }

        return ":hoxton/template-default";
    };

    if (
        EditableType.File === editable.type ||
        EditableType.Image === editable.type ||
        EditableType.Video === editable.type
    ) {
        value = getMediaItemValue(overwrite);
    }

    if (EditableType.Array === editable.type && Array.isArray(value)) {
        return getArrayValue(value);
    }

    if (EditableType.Boolean === editable.type) {
        return (value as boolean) || value === "true" ? "true" : "false";
    }

    if (value !== null && value !== undefined && value !== "") {
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            return String(value);
        }

        return "";
    }

    if (campaignMasterTemplateIds) {
        // lets try to work out a common default value if there is not obvious value.
        return editableUtils.getGroupPlaceholder(editable, campaignMasterTemplateIds, "") as string;
    }

    return "";
}

export default class ImportExport {
    $apollo: typeof apolloClient;
    $store: typeof store;
    excelJS: typeof import("exceljs") | null;

    constructor(apollo: typeof apolloClient, storeInstance: typeof store) {
        this.$apollo = apollo;
        this.$store = storeInstance;
        this.excelJS = null;
    }

    async loadExcelJS() {
        this.excelJS ??= await import("exceljs");

        return this.excelJS;
    }

    async applyEditableUpdate(editableUpdate: OverwriteWithEditable) {
        const updatedOverwrite: OverwriteWithEditable | null = await this.getOverwriteUpdatedByImport(
            editableUpdate,
            editableUpdate.language,
            editableUpdate.editableGroupValueIds
        );

        if (!updatedOverwrite) {
            return;
        }

        const updatedEditableWithOverwrite = {
            editable: editableUpdate.editable,
            overwrites: [updatedOverwrite]
        };

        const updatedEditables = editableUtils.mergeEditables(this.$store.state.editor.localEditableOverwrites, [
            // this is stupid, overwrite is not OverwriteWithEditable and we shouldn't just merge them, but yolo
            updatedEditableWithOverwrite as unknown as EditableWithOverwrites
        ]);

        await this.$store.dispatch(EditorAction.SetLocalEditableOverwrites, updatedEditables);
    }

    async exportLibrarySelectTemplate() {
        await this.loadExcelJS();

        const { editableGroupIds } = this.$store.state.campaign.normalized;
        const masterTemplateLabelMap = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.masterTemplatesBySize
        ] as Record<string, string[]>;
        const masterTemplateLabels = Object.keys(masterTemplateLabelMap);
        const workbook = new this.excelJS!.Workbook();
        const hogarthLogo = workbook.addImage({
            base64: oldHogarthLogo,
            extension: "png"
        });

        workbook.views = [
            {
                x: 0,
                y: 0,
                width: 10000,
                height: 20000,
                firstSheet: 1,
                activeTab: 1,
                visibility: "visible"
            }
        ];

        const valuesSheet = workbook.addWorksheet("Values");
        const valueFixedHeaders = ["size", "language"];
        const inputFixedHeaders = ["size", "language"];

        addInfoRow(
            valuesSheet,
            valueFixedHeaders,
            editableGroupIds.map(groupName => ({ name: groupName })),
            hogarthLogo,
            "Overviews",
            "values to be used when selecting the variation"
        );
        valuesSheet.addRow([...valueFixedHeaders, ...editableGroupIds]);
        setHeaderRowStyle(valuesSheet, 2);

        const inputSheet = workbook.addWorksheet("Input");
        addInfoRow(
            inputSheet,
            inputFixedHeaders,
            editableGroupIds.map(groupName => ({ name: groupName })),
            hogarthLogo,
            "Overview",
            "values to be used when selecting the variation"
        );
        inputSheet.addRow([...inputFixedHeaders, ...editableGroupIds]);
        setHeaderRowStyle(inputSheet, 2);

        addValuesAndFilters(masterTemplateLabels, valuesSheet, inputSheet, 1, 1);
        addValuesAndFilters(this.$store.state.campaign.languages, valuesSheet, inputSheet, 2, 2);

        editableGroupIds.forEach((groupName, paramColumnIndex) => {
            const groupValueIdsForGroup =
                this.$store.state.campaign.normalized.editableGroups[groupName]!.editableGroupValues;

            const stringValuesForGroup = groupValueIdsForGroup.map(
                groupValueId => this.$store.state.campaign.editableGroupValues[groupValueId]!.value
            );

            addValuesAndFilters(
                stringValuesForGroup,
                valuesSheet,
                inputSheet,
                paramColumnIndex + valueFixedHeaders.length + 1,
                paramColumnIndex + inputFixedHeaders.length + 1
            );
        });

        // Add reporting labels column
        const reportingLabelsColumnIndex = editableGroupIds.length + valueFixedHeaders.length + 1;
        const inputReportingLabelsColumnIndex = editableGroupIds.length + inputFixedHeaders.length + 1;

        addReportingLabelsColumn(valuesSheet, inputSheet, reportingLabelsColumnIndex, inputReportingLabelsColumnIndex);

        const exportedFilename = `${this.$store.state.campaign.name}-download-request`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    async exportXLSTranslationsTemplate(language: string, canSeeRestrictedEditables = true) {
        await this.loadExcelJS();

        const translations = this.getSimplifiedTranslationMapping(language, canSeeRestrictedEditables);
        const workbook = new this.excelJS!.Workbook();
        const hogarthLogo = workbook.addImage({
            base64: oldHogarthLogo,
            extension: "png"
        });

        const sheet = workbook.addWorksheet("translations");
        // merge by start row, start column, end row, end column (equivalent to A1:Z1)
        sheet.columns = [
            { key: "source", width: 40 },
            { key: "language", width: 64 }
        ];
        sheet.views = [{ state: "frozen", xSplit: 1, ySplit: 2 }];
        sheet.addRow(["", "Translations"]);
        setInfoRowStyle(sheet, 1);
        sheet.mergeCells(1, 2, 1, 2);

        sheet.addImage(hogarthLogo, logoPosition);

        sheet.addRow(["source", language]);
        setHeaderRowStyle(sheet, 2);

        let isContentStyleSet = false;
        Object.keys(translations).forEach(enValue => {
            const contentRow = sheet.addRow([enValue, translations[enValue]], isContentStyleSet ? "i" : undefined);

            if (!isContentStyleSet) {
                applyStyle(contentRow, valueStyle);
                const groupValueCell = contentRow.getCell(1);
                applyStyle(groupValueCell, languageGroupNameStyle);

                isContentStyleSet = true;
            }
        });

        const exportedFilename = `${this.$store.state.campaign.name}-translations-${language}`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    async exportXLSValuesTemplate(exportType: ExportType, canSeeRestrictedEditables = true) {
        await this.loadExcelJS();

        const { editableGroups, editableGroupIds, editables, masterTemplateIds } =
            this.$store.state.campaign.normalized;
        const masterTemplateLabelMap = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.masterTemplatesBySize
        ] as Record<string, string[]>;
        const masterTemplateLabels = Object.keys(masterTemplateLabelMap);
        const workbook: ExcelJS.Workbook = new this.excelJS!.Workbook();

        const config: WorksheetConfig = {
            masterTemplateLabelMap,
            masterTemplateLabels,
            masterTemplateIds,
            exportType
        };

        const seenGroups = new Set<string>();
        editableGroupIds.forEach(groupName => {
            if (seenGroups.has(groupName)) {
                return; // Skip duplicate group
            }
            seenGroups.add(groupName);

            const editablesInGroup = getExportableEditables(
                editableGroups[groupName]!.editables.map(editableId => editables[editableId]!),
                canSeeRestrictedEditables
            );

            const editableGroupValues = editableGroups[groupName]!.editableGroupValues.map(
                editableGroupValueId => this.$store.state.campaign.editableGroupValues[editableGroupValueId]!
            );

            if (!editablesInGroup.length) {
                return;
            }

            this.createWorksheetForEditableGroup(workbook, groupName, editablesInGroup, editableGroupValues, config);
        });

        const exportedFilename = `${this.$store.state.campaign.name}-${campaignDefaultLanguage}-${exportType}`;
        const content = await workbook.xlsx.writeBuffer();
        const blob = new Blob([content], { type: mimetype.XSLX });

        createFile(blob, exportedFilename);
    }

    private createWorksheetForEditableGroup(
        workbook: ExcelJS.Workbook,
        groupName: string,
        editablesInGroup: Editable[],
        editableGroupValues: { _id: string; value: string }[],
        config: WorksheetConfig
    ) {
        const hogarthLogo = workbook.addImage({
            base64: oldHogarthLogo,
            extension: "png"
        });

        // Deduplicate editables by ID to prevent duplicate columns
        const uniqueEditables = Array.from(new Map(editablesInGroup.map(e => [e._id, e])).values());

        const sheet = this.setupWorksheet(workbook, groupName, uniqueEditables, config.exportType, hogarthLogo);

        this.populateWorksheetData(sheet, uniqueEditables, editableGroupValues, config);
    }

    private setupWorksheet(
        workbook: ExcelJS.Workbook,
        groupName: string,
        editablesInGroup: Editable[],
        exportType: ExportType,
        hogarthLogo: number
    ): ExcelJS.Worksheet {
        const sheet = workbook.addWorksheet(groupName);

        // merge by start row, start column, end row, end column (equivalent to A1:Z1)
        sheet.columns = [
            { key: "groupValue", width: 40, style: groupNameStyle },
            { key: "template", width: 32 },
            ...editablesInGroup.map(({ name }) => ({
                key: name,
                width: 32
            }))
        ];

        sheet.views = [{ state: "frozen", xSplit: exportType === "global" ? 1 : 2, ySplit: 2 }];

        sheet.addRow(["", groupName]);
        setInfoRowStyle(sheet, 1);
        sheet.mergeCells(1, 2, 1, editablesInGroup.length + 1);
        sheet.getCell(1, 2).value = {
            richText: [
                {
                    font: {
                        ...silkaFont,
                        bold: true,
                        size: 20
                    },
                    text: `${this.$store.state.campaign.client.name}: ${this.$store.state.campaign.name} \r\n`
                },
                {
                    font: {
                        ...silkaFont
                    },
                    text: groupName
                }
            ]
        };

        sheet.addImage(hogarthLogo, logoPosition);
        const fixedHeaders = exportType === "global" ? ["Version Name"] : ["Version Name", "Template"];
        sheet.addRow([...fixedHeaders, ...editablesInGroup.map(({ name }) => name)]);
        setHeaderRowStyle(sheet, 2);

        return sheet;
    }

    private populateWorksheetData(
        sheet: ExcelJS.Worksheet,
        editablesInGroup: Editable[],
        editableGroupValues: { _id: string; value: string }[],
        config: WorksheetConfig
    ) {
        let isContentStyleSet = false;
        const masterTemplateLabelsToExport =
            config.exportType === "global" ? [ExportGroupScopeLabel] : config.masterTemplateLabels;

        editableGroupValues.forEach(editableGroup => {
            masterTemplateLabelsToExport.forEach(templateName => {
                // we take the value from the first master template that matches the name.
                const masterTemplateId = config.masterTemplateLabelMap[templateName]
                    ? config.masterTemplateLabelMap[templateName][0]
                    : undefined;

                const editableExportValues = editablesInGroup.map(editable =>
                    this.getExportValue(editable._id, masterTemplateId, [editableGroup._id], config.masterTemplateIds)
                );
                // we want to ignore templates column for global values export
                const fixedCells =
                    config.exportType === "global" ? [editableGroup.value] : [editableGroup.value, templateName];
                const contentRow = sheet.addRow(
                    [...fixedCells, ...editableExportValues],
                    isContentStyleSet ? "i" : undefined
                );

                if (!isContentStyleSet) {
                    applyStyle(contentRow, valueStyle);
                    const groupValueCell = contentRow.getCell(1);
                    applyStyle(groupValueCell, groupNameStyle);

                    isContentStyleSet = true;
                }
            });
        });
    }

    async getMediaItemById(mediaItemId: string) {
        return this.$apollo?.query<{ mediaItem: OverwriteMediaItem } | undefined>({
            query: mediaItemQuery,
            variables: {
                mediaItemId
            }
        });
    }

    async getMediaItemFromImportString(
        editableUpdate: OverwriteWithEditable
    ): Promise<{ mediaItem: OverwriteMediaItem; mediaItemId: string }> {
        const mediaItemIdRegex = /:hoxton\/([^/]*)/;

        const valueStr = Array.isArray(editableUpdate.value)
            ? JSON.stringify(editableUpdate.value)
            : String(editableUpdate.value ?? "");

        if (!editableUpdate.value || typeof editableUpdate.value !== "string") {
            throw new Error(`Invalid value: "${valueStr}" is not a valid hoxton file reference`);
        }

        const match = mediaItemIdRegex.exec(editableUpdate.value);

        if (!match?.[1]) {
            throw new Error(`"${valueStr}" is not a valid hoxton file reference format`);
        }

        const mediaItemId = match[1];

        const result = await this.getMediaItemById(mediaItemId);

        if (!result?.data?.mediaItem) {
            throw new Error(`"${valueStr}" is not a valid file reference`);
        }

        return { mediaItem: result.data.mediaItem, mediaItemId };
    }

    async getOverwriteUpdatedByImport(
        updateData: OverwriteWithEditable,
        language: string,
        editableGroupValueIds: string[]
    ) {
        const editableUpdate = deepClone(updateData);
        // Set mediaItem to null if not found for consistency

        if (editableUpdate.value === ":hoxton/template-default" || editableUpdate.value === null) {
            // default value was used go generate this value so there is no need to create any overwrites
            return null;
        }

        if (editableUpdate.mediaItem === null) {
            await this.handleMediaItemUpdate(editableUpdate);
        }

        // Unknown scope so return an empty object
        if (!Object.values(OverwriteScope).includes(editableUpdate.scope)) {
            return null;
        }
        // Set base property of overwrite
        const updatedEditable = editableUtils.setScopeValues(editableUpdate.scope, {
            language,
            scope: editableUpdate.scope,
            editableGroupValueIds,
            masterTemplateId: editableUpdate.masterTemplateId!
        }) as EditableOverwrite;
        // Set tmp overwriteId
        const { localOverwriteTmpId } = this.$store.state.editor;
        updatedEditable._id = `tmpId-${localOverwriteTmpId}`;
        await this.$store.dispatch(EditorAction.SetLocalOverwriteTmpId, localOverwriteTmpId + 1);
        // Set value properties
        updatedEditable.value = editableUpdate.value;
        updatedEditable.mediaItem = editableUpdate.mediaItem;

        if (editableUpdate.settings) {
            updatedEditable.settings = editableUpdate.settings;
        } else {
            const existingOverwrite = this.getSavedOverwrite(
                editableUpdate.editable._id,
                editableUpdate.masterTemplateId ?? null,
                language,
                editableGroupValueIds
            );
            const settingsFromScope = existingOverwrite?.settingsFromScope?.[editableUpdate.scope];
            updatedEditable.settings = settingsFromScope ?? existingOverwrite?.settings ?? undefined;
        }

        // Return updated editable
        return {
            ...updatedEditable,
            editable: editableUpdate.editable,
            editableGroupValueIds: editableUpdate.editableGroupValueIds,
            masterTemplateId: editableUpdate.masterTemplateId,
            language: editableUpdate.language
        };
    }

    private async handleMediaItemUpdate(editableUpdate: OverwriteWithEditable) {
        if (
            editableUpdate.editable.type === EditableType.File ||
            editableUpdate.editable.type === EditableType.Image ||
            editableUpdate.editable.type === EditableType.Video
        ) {
            await this.handleFileTypeMediaItem(editableUpdate);
        } else {
            editableUpdate.mediaItem = null;
        }
    }

    private async handleFileTypeMediaItem(editableUpdate: OverwriteWithEditable) {
        if (
            editableUpdate.value &&
            typeof editableUpdate.value === "string" &&
            editableUpdate.value.startsWith(":hoxton/")
        ) {
            const { mediaItem, mediaItemId } = await this.getMediaItemFromImportString(editableUpdate);
            editableUpdate.mediaItem = mediaItem;
            editableUpdate.value = mediaItemId;
        } else if (
            editableUpdate.value &&
            typeof editableUpdate.value === "string" &&
            isValidHttpUrl(editableUpdate.value)
        ) {
            throw new Error(
                `The "${editableUpdate.editable.type}" type for "${editableUpdate.editable.name}" is not supported for external feeds. You should use type: "text" instead.`
            );
        } else {
            const valueStr = Array.isArray(editableUpdate.value)
                ? JSON.stringify(editableUpdate.value)
                : String(editableUpdate.value ?? "");
            throw new Error(`"${valueStr}" is not a valid file reference`);
        }
    }

    async simplifiedLanguageUpdate({
        language,
        languageMapping,
        canSeeRestrictedEditables = true
    }: {
        language: string;
        languageMapping: Record<string, string>;
        canSeeRestrictedEditables?: boolean;
    }) {
        const { editableGroups, editableGroupIds, editables, masterTemplateIds } =
            this.$store.state.campaign.normalized;

        const normalizedLanguageMapping = Object.keys(languageMapping).reduce((acc: Record<string, string>, curr) => {
            const normalizedPhrase = normalizeTranslationPhrase(curr);
            acc[normalizedPhrase] = languageMapping[curr]!;

            return acc;
        }, {});

        const updates: OverwriteWithEditable[] = [];

        const config = {
            editableGroups,
            editables,
            masterTemplateIds,
            language,
            languageMapping,
            normalizedLanguageMapping,
            canSeeRestrictedEditables
        };

        // for each group
        editableGroupIds.forEach(editableGroupId => {
            this.processEditableGroupForLanguageUpdate(editableGroupId, updates, config);
        });

        await Promise.all(updates.map(editableUpdate => this.applyEditableUpdate(editableUpdate)));
    }

    getImportScopeAndSettings(
        overwriteTargetLang: EditableOverwrite | null,
        overwriteDefaultLang: EditableOverwrite | null,
        masterTemplateId: string | null
    ): { isLocked: boolean; scope: OverwriteScopeType; settings: CSSSettings | null } | null {
        const isTargetLocked = overwriteTargetLang?.valueFromScope === OverwriteScope.EditableGroupTemplate;
        if (isTargetLocked) {
            return null;
        }

        let scope: OverwriteScopeType = OverwriteScope.EditableGroup;

        const isDefaultTemplateScope = overwriteDefaultLang?.scope === OverwriteScope.EditableGroupTemplate;

        if (isDefaultTemplateScope && masterTemplateId) {
            scope = overwriteDefaultLang.scope;
        }

        const settingsToUse = overwriteTargetLang?.settings ?? overwriteDefaultLang?.settingsFromScope?.[scope] ?? null;

        return { isLocked: false, scope, settings: settingsToUse };
    }

    private processEditableGroupForLanguageUpdate(
        editableGroupId: string,
        updates: OverwriteWithEditable[],
        config: LanguageUpdateConfig
    ) {
        const editableGroup = config.editableGroups[editableGroupId];
        const importableEditables = editableGroup!.editables.filter(
            (editableId: string) =>
                ExportableTranslationTypes.includes(config.editables[editableId]!.type) &&
                (config.canSeeRestrictedEditables || !config.editables[editableId]!.restricted)
        );

        if (!importableEditables.length) {
            return;
        }

        // these are the editables that we should check values for.
        importableEditables.forEach((editableId: string) => {
            // passing all possible groups values in a loop
            editableGroup!.editableGroupValues.forEach((egvId: string) => {
                // iterate over group as well as all template level overwrites
                ["", ...config.masterTemplateIds].forEach(masterTemplateId => {
                    const overwriteDefaultLang = this.getSavedOverwrite(
                        editableId,
                        masterTemplateId,
                        campaignDefaultLanguage,
                        [egvId]
                    );

                    const defaultLangVal =
                        (editableUtils.computeEditableValue(
                            config.editables[editableId]!,
                            masterTemplateId,
                            overwriteDefaultLang
                        ) as string | null) ?? "";

                    if (config.languageMapping[defaultLangVal]) {
                        // update value when we get an exact values match
                        const overwriteTargetLang = this.getSavedOverwrite(
                            editableId,
                            masterTemplateId,
                            config.language,
                            [egvId]
                        );

                        const result = this.getImportScopeAndSettings(
                            overwriteTargetLang,
                            overwriteDefaultLang,
                            masterTemplateId
                        );

                        if (!result) {
                            return;
                        }

                        updates.push({
                            scope: result.scope,
                            language: config.language,
                            value: config.languageMapping[defaultLangVal],
                            editable: config.editables[editableId]!,
                            editableGroupValueIds: [egvId],
                            mediaItem: null,
                            settings: result.settings,
                            ...(result.scope === OverwriteScope.EditableGroupTemplate && { masterTemplateId })
                        });

                        return;
                    }

                    const normalisedVal = normalizeTranslationPhrase(defaultLangVal);
                    if (config.normalizedLanguageMapping[normalisedVal]) {
                        // if we don't get exact match normalize the string and try again
                        const overwriteTargetLang = this.getSavedOverwrite(
                            editableId,
                            masterTemplateId,
                            config.language,
                            [egvId]
                        );

                        const result = this.getImportScopeAndSettings(
                            overwriteTargetLang,
                            overwriteDefaultLang,
                            masterTemplateId
                        );

                        if (!result) {
                            return;
                        }

                        updates.push({
                            scope: result.scope,
                            language: config.language,
                            value: config.normalizedLanguageMapping[normalisedVal],
                            editable: config.editables[editableId]!,
                            editableGroupValueIds: [egvId],
                            mediaItem: null,
                            settings: result.settings,
                            ...(result.scope === OverwriteScope.EditableGroupTemplate && { masterTemplateId })
                        });
                    }
                });
            });
        });
    }

    getExportValue(
        editableId: string,
        masterTemplateId = "",
        groupValueIds: string[] = [],
        campaignMasterTemplateIds: string[] = []
    ) {
        const { editables } = this.$store.state.campaign.normalized;

        const savedOverwrite = this.getSavedOverwrite(
            editableId,
            masterTemplateId,
            campaignDefaultLanguage,
            groupValueIds
        );

        return getExportValueFromOverwrite({
            editable: editables[editableId]!,
            masterTemplateId,
            overwrite: savedOverwrite,
            campaignMasterTemplateIds
        });
    }

    getGroupValueId(groupName: string, groupValueName: string) {
        const groupValueNamesByGroup = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.groupValueNamesByGroup
        ] as Record<string, { editableGroupValuesNamesMap: Record<string, string> }>;

        return groupValueNamesByGroup[groupName]!.editableGroupValuesNamesMap[groupValueName];
    }

    getSavedOverwrite(editableId: string, templateId: string | null, language: string, selectedGroupsIds: string[]) {
        const editorOverwrites = (this.$store.getters as Record<string, unknown>)[
            EditorGetters.editableOverwrites
        ] as EditableWithOverwrites[];

        const getterFunction = (this.$store.getters as Record<string, unknown>)[
            CampaignGetters.editableOverwriteValue
        ] as (
            editableId: string,
            templateId: string | null,
            language: string,
            selectedGroupsIds: string[],
            overwrites?: EditableWithOverwrites[]
        ) => EditableOverwrite | null;

        return getterFunction(editableId, templateId, language, selectedGroupsIds, editorOverwrites);
    }

    getTranslationValue(editableId: string, language: string, masterTemplateId = "", groupValueIds: string[] = []) {
        const { editables } = this.$store.state.campaign.normalized;

        const overwriteDefaultLang = this.getSavedOverwrite(
            editableId,
            masterTemplateId,
            campaignDefaultLanguage,
            groupValueIds
        );

        const overwriteLang = this.getSavedOverwrite(editableId, masterTemplateId, language, groupValueIds);

        const cmpDefaultLangValue =
            (editableUtils.computeEditableValue(editables[editableId]!, masterTemplateId, overwriteDefaultLang) as
                | string
                | null) ?? "";

        let languageValue = "";

        if (language === campaignDefaultLanguage || overwriteDefaultLang?._id !== overwriteLang?._id) {
            languageValue =
                (editableUtils.computeEditableValue(editables[editableId]!, masterTemplateId, overwriteLang) as
                    | string
                    | null) ?? "";
        }

        return {
            [campaignDefaultLanguage]: cmpDefaultLangValue,
            [language]: languageValue
        };
    }

    getSimplifiedTranslationMapping(language = "en", canSeeRestrictedEditables = true) {
        const translations: Record<string, string> = {};
        const { editableGroups, editableGroupIds, masterTemplateIds, editables } =
            this.$store.state.campaign.normalized;

        editableGroupIds.forEach(editableGroupId => {
            this.processEditableGroupForTranslations(
                editableGroupId,
                editableGroups,
                editables,
                masterTemplateIds,
                language,
                translations,
                canSeeRestrictedEditables
            );
        });

        return translations;
    }

    private processEditableGroupForTranslations(
        editableGroupId: string,
        editableGroups: Record<string, { editables: string[]; editableGroupValues: string[] }>,
        editables: Record<string, Editable>,
        masterTemplateIds: string[],
        language: string,
        translations: Record<string, string>,
        canSeeRestrictedEditables: boolean
    ) {
        const editableGroup = editableGroups[editableGroupId];
        const exportable = editableGroup!.editables.filter(
            editableId =>
                ExportableTranslationTypes.includes(editables[editableId]!.type) &&
                (canSeeRestrictedEditables || !editables[editableId]!.restricted)
        );

        if (!exportable.length) {
            return;
        }

        // there are editables that we should check values for.
        editableGroup!.editableGroupValues.forEach(egvId => {
            this.processEditableTranslations(exportable, egvId, masterTemplateIds, language, translations);
        });
    }

    private processEditableTranslations(
        exportableEditables: string[],
        egvId: string,
        masterTemplateIds: string[],
        language: string,
        translations: Record<string, string>
    ) {
        exportableEditables.forEach(editableId => {
            // iterate over group as well as all template level overwrites
            ["", ...masterTemplateIds].forEach(masterTemplateId => {
                const translationObject = this.getTranslationValue(editableId, language, masterTemplateId, [egvId]);

                // we don't want empty entries and values that look like url
                if (
                    translationObject[campaignDefaultLanguage] &&
                    translationObject[language] &&
                    !urlRegex.test(translationObject[campaignDefaultLanguage])
                ) {
                    translations[translationObject[campaignDefaultLanguage]] = translationObject[language];
                }
            });
        });
    }

    parseGroupValueImportRow(
        row: (string | null)[],
        groupName: string,
        lastRelevantColumn: number,
        allSizesImport: boolean
    ) {
        let groupValueName: string | null | undefined;
        let masterTemplateName: string | null | undefined;
        let allEditableValues: (string | null)[];

        if (allSizesImport) {
            [groupValueName, ...allEditableValues] = row;
            masterTemplateName = ExportGroupScopeLabel;
        } else {
            [groupValueName, masterTemplateName, ...allEditableValues] = row;
        }

        if (!groupValueName) {
            return null;
        }

        const editableValues = allEditableValues.slice(0, lastRelevantColumn);

        return {
            groupValueName,
            masterTemplateName,
            editableValues,
            groupValueId: this.getGroupValueId(groupName, groupValueName)
        };
    }
}
